import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {emptyMenuConfig, Licenza, MenuConfig, MenuVoceBinding, Modulo, ModuloConfig, ModuloPermesso, parseMenuConfig, SearchResult} from '../_models/_Index';
import {environment} from '../../environments/environment';
import {BehaviorSubject, firstValueFrom, Subject} from 'rxjs';

// const cachePrefix = 'archiconnect-v2-';

@Injectable({providedIn: 'root'})
export class LicenzeService {
  // di chi resta collegato a lungo, se nel frattempo qualcun altro ha cambiato la licenza.
  private static readonly cacheTtlMs = 5 * 60 * 1000;
  public handleSave: Subject<number> = new Subject();
  private currentPermessiSubject = new BehaviorSubject<Modulo[]>([]);

  // Durata della cache di lettura (moduli, binding, rifinitura). Chi ha appena scritto qualcosa non
  // ci passa mai (usa refreshModuliOfOrgAsync/refreshMenuBindingsAsync, sempre freschi): serve solo a
  // non ripetere le stesse chiamate nella raffica del login e a far ripartire da zero l'allineamento
  /** Observable pubblico per ascoltare il caricamento/refresh dei permessi dell'organizzazione corrente. */
  public readonly currentPermessi$ = this.currentPermessiSubject.asObservable();

  // Legame voce di menù → modulo/ruoli: globale, non dipende dall'organizzazione. Non va ricaricato a
  // ogni cambio org (che non lo cambia) ma solo alla scadenza del TTL — vedi refreshModuliOfOrgAsync.
  private currentMenuBindingsSubject = new BehaviorSubject<MenuVoceBinding[]>([]);
  public readonly currentMenuBindings$ = this.currentMenuBindingsSubject.asObservable();
  // Istante dell'ultimo caricamento riuscito; 0 = mai caricati. Codifica sia "già caricati" sia "da
  // quanto", così non serve un flag booleano separato da tenere in sincrono.
  private menuBindingsLoadedAt = 0;
  // Caricamento binding in corso verso il primo completamento: separato da menuBindingsLoadedAt perché
  // due refreshModuliOfOrgAsync concorrenti per organizzazioni DIVERSE (quindi non coalescenti tra
  // loro) devono comunque condividere questo UNICO fetch invece di partirne uno ciascuna.
  private menuBindingsPromise: Promise<MenuVoceBinding[]> | null = null;

  // Coalescing del caricamento completo per organizzazione. Una mappa mantiene indipendenti anche
  // due cambi organizzazione sovrapposti, senza perdere la richiesta ancora in corso della prima.
  private refreshModuliPromises = new Map<number, Promise<Modulo[]>>();
  // La richiesta dei soli moduli è condivisa anche col percorso rapido del login: AppComponent può
  // continuare in parallelo con binding e configurazione senza provocare una seconda ModuliOfOrg.
  private permessiPromises = new Map<number, Promise<Modulo[]>>();
  private permessiLoadedForOrgId: number | null = null;
  private permessiLoadedAt = 0;
  // Ultima organizzazione per cui il caricamento completo è andato a buon fine: a differenza del
  // coalescing sopra (che aiuta solo chiamate sovrapposte nel tempo), copre anche richieste complete
  // in sequenza ravvicinata ma non concorrenti — es. AppComponent seguito da DashboardHome.
  // Usata solo da ensureModuliOfOrgLoadedAsync: refreshModuliOfOrgAsync resta sempre "fresco" per chi
  // ha appena scritto qualcosa (licenze-config*, licenze-menu-config) e non deve mai vedere dati vecchi.
  private moduliLoadedForOrgId: number | null = null;
  private moduliLoadedAt = 0;

  // Identifica la sessione corrente: resetSessionCache() lo incrementa. Le richieste catturano il
  // valore alla partenza e scrivono lo stato condiviso solo se non è cambiato — senza, una risposta
  // arrivata dopo il logout riscriverebbe cache e subject appena azzerati (le closure hanno già
  // catturato orgId, quindi azzerare le promise non basterebbe).
  private sessionEpoch = 0;

  // Rifinitura sottrattiva del menù per l'organizzazione corrente.
  private currentMenuConfigSubject = new BehaviorSubject<MenuConfig>(emptyMenuConfig());
  public readonly currentMenuConfig$ = this.currentMenuConfigSubject.asObservable();

  constructor(private http: HttpClient) {}

  public get currentMenuBindingsValue(): MenuVoceBinding[] {
    return this.currentMenuBindingsSubject.value;
  }

  public get currentMenuConfigValue(): MenuConfig {
    return this.currentMenuConfigSubject.value;
  }

  public checkPermesso(permessoCode: string): boolean {
    return this.currentPermessiSubject.value.some(m => m.codice === permessoCode);
  }

  /**
   * Chiude la sessione lato cache: al prossimo login, anche per la stessa organizzazione,
   * ensureModuliOfOrgLoadedAsync, ensurePermessiOfOrgLoadedAsync e i binding tornano a caricare dati
   * freschi invece di riusare quelli della sessione appena terminata. Le richieste già in volo non
   * vengono annullate ma i loro risultati vengono scartati (vedi sessionEpoch): quando arrivano non
   * ripopolano né i subject né i marcatori di "già caricato".
   */
  resetSessionCache(): void {
    this.sessionEpoch++;
    this.currentPermessiSubject.next([]);
    this.currentMenuBindingsSubject.next([]);
    this.currentMenuConfigSubject.next(emptyMenuConfig());
    this.menuBindingsLoadedAt = 0;
    this.menuBindingsPromise = null;
    this.refreshModuliPromises.clear();
    this.permessiPromises.clear();
    this.permessiLoadedForOrgId = null;
    this.permessiLoadedAt = 0;
    this.moduliLoadedForOrgId = null;
    this.moduliLoadedAt = 0;
  }

  async getAllModuliFilteredAsync(search: string, page: number, count: number, sortField: string, sortAsc: boolean) {
    return firstValueFrom(
      this.http.post<SearchResult<Modulo>>(`${environment.localApiUrl}license/Licenze/AllModuli`,
        {search, page, count, sortField, sortAsc})
    );
  }

  async getModuliFilteredAsync(search: string, page: number, count: number, sortField: string, sortAsc: boolean) {
    return firstValueFrom(this.http.post<SearchResult<Modulo>>(`${environment.localApiUrl}license/Licenze/ModuliFiltered`,
      {search, page, count, sortField, sortAsc}));
  }

  async getModulosync(code: string) {
    return firstValueFrom(this.http.get<Modulo>(`${environment.localApiUrl}license/Licenze/Modulo?code=${code}`));
  }

  async getModuloFromIdAsync(moduloId: number) {
    return firstValueFrom(this.http.get<Modulo>(`${environment.localApiUrl}license/Licenze/ModuloFromId?moduloId=${moduloId}`));
  }

  async createModuloAsync(modulo: Modulo) {
    return firstValueFrom(this.http.post<Modulo>(`${environment.localApiUrl}license/Licenze/CreateModulo`, modulo));
  }

  async updateModuloAsync(modulo: Modulo) {
    return firstValueFrom(this.http.post<Modulo>(`${environment.localApiUrl}license/Licenze/UpdateModulo`, modulo));
  }

  async deleteModuloAsync(code: string) {
    return firstValueFrom(this.http.get<Modulo>(`${environment.localApiUrl}license/Licenze/DeleteModulo?code=${code}`));
  }

  async getModuliConfigFilteredAsync(orgId: number, roleId: number, operatoreId: number) {
    return firstValueFrom(this.http
      .get<SearchResult<ModuloConfig>>(`${environment.localApiUrl}license/Licenze/ModuliConfigFiltered/${orgId}/${roleId}/${operatoreId}`));
  }

  /**
   * Ricarica tutto ciò che governa la visibilità del menù per l'organizzazione indicata:
   * permessi (moduli licenziati), binding globali delle voci e rifinitura dell'organizzazione.
   *
   * Il refresh esplicito termina quando tutti e tre gli elementi sono allineati, così chi ha appena
   * salvato una licenza non prosegue con uno stato parziale. Binding e rifinitura restano best-effort:
   * un loro errore applica i default equivalenti al comportamento precedente.
   *
   * I binding sono globali (non dipendono da orgId): entro il TTL i cambi organizzazione riusano il
   * valore in cache invece di rifare la chiamata di rete.
   *
   * Chiamate concorrenti per la STESSA organizzazione condividono la stessa richiesta in corso
   * (coalescing) invece di partire da capo ognuna: succede regolarmente al login, dove più punti
   * dell'app la richiedono senza coordinarsi. Per un'organizzazione diversa procede normalmente,
   * altrimenti si servirebbero dati non aggiornati per la nuova organizzazione.
   */
  async refreshModuliOfOrgAsync(orgId: number): Promise<Modulo[]> {
    return this.loadCompleteMenuStateAsync(orgId, false);
  }

  /**
   * Come refreshModuliOfOrgAsync, ma se questa organizzazione è già stata caricata con successo
   * (da qualunque chiamante) riusa i dati in memoria invece di rifare la chiamata di rete. Per chi
   * deve solo assicurarsi che i dati ci siano — avvio app, dashboard, SSO — non per chi ha appena
   * scritto qualcosa e ha bisogno di dati garantiti freschi (usare refreshModuliOfOrgAsync).
   */
  async ensureModuliOfOrgLoadedAsync(orgId: number): Promise<Modulo[]> {
    if (this.moduliLoadedForOrgId === orgId && this.isFresh(this.moduliLoadedAt)) {
      return this.currentPermessiSubject.value;
    }

    return this.loadCompleteMenuStateAsync(orgId, true);
  }

  /**
   * Garantisce i soli moduli indispensabili all'avvio delle pagine. Il login usa questo percorso
   * per non restare bloccato dalla configurazione del menù; AppComponent completa in parallelo il
   * caricamento e tiene il menù nascosto fino a quel momento.
   */
  async ensurePermessiOfOrgLoadedAsync(orgId: number): Promise<Modulo[]> {
    return this.loadPermessiAsync(orgId, true);
  }

  /**
   * Ricarica i binding globali dal server, bypassando la cache di sessione — va chiamata dopo ogni
   * scrittura (salvataggio/ripristino/eliminazione in Mappatura menù) per vedere subito il valore
   * aggiornato. In caso di errore mantiene i default del dizionario.
   */
  async refreshMenuBindingsAsync(): Promise<MenuVoceBinding[]> {
    const epoch = this.sessionEpoch;
    try {
      const bindings = await this.getMenuVociBindingsAsync();
      if (this.isSameSession(epoch)) {
        this.currentMenuBindingsSubject.next(bindings);
        this.menuBindingsLoadedAt = Date.now();
      }
      return bindings;
    } catch (err) {
      console.error('Caricamento binding voci di menù non riuscito, si applicano i default.', err);
      return this.currentMenuBindingsSubject.value;
    }
  }

  /** Ricarica la rifinitura dell'organizzazione. In caso di errore mantiene la configurazione vuota. */
  async refreshMenuConfigAsync(orgId: number): Promise<MenuConfig> {
    const epoch = this.sessionEpoch;
    try {
      const licenza = await this.getLicenzaFromIdAsync(orgId);
      const config = parseMenuConfig(licenza?.menuConfig);
      if (this.isSameSession(epoch)) {
        this.currentMenuConfigSubject.next(config);
      }
      return config;
    } catch (err) {
      console.error('Caricamento configurazione menù non riuscito, si applica quella vuota.', err);
      const vuota = emptyMenuConfig();
      if (this.isSameSession(epoch)) {
        this.currentMenuConfigSubject.next(vuota);
      }
      return vuota;
    }
  }

  async getMenuVociBindingsAsync() {
    return firstValueFrom(
      this.http.get<SearchResult<MenuVoceBinding>>(`${environment.localApiUrl}license/Licenze/MenuVociBindings`)
    ).then((result: SearchResult<MenuVoceBinding>) => result.result ?? []);
  }

  async updateMenuVoceBindingAsync(binding: MenuVoceBinding) {
    return firstValueFrom(
      this.http.post<MenuVoceBinding>(`${environment.localApiUrl}license/Licenze/UpdateMenuVoceBinding`, binding)
    );
  }

  async deleteMenuVoceBindingAsync(voceId: string) {
    return firstValueFrom(this.http
      .get<{ result: string }>(`${environment.localApiUrl}license/Licenze/DeleteMenuVoceBinding?voceId=${encodeURIComponent(voceId)}`));
  }

  async getModuliOfOrgAsync(orgId: number) {
    return firstValueFrom(
      this.http.get<SearchResult<Modulo>>(`${environment.localApiUrl}license/Licenze/ModuliOfOrg/${orgId}`)
    ).then((result: SearchResult<Modulo>) => result.result);
  }

  async createPermessoAsync(permesso: ModuloPermesso) {
    return firstValueFrom(
      this.http.post<ModuloPermesso>(`${environment.localApiUrl}license/Licenze/CreatePermesso`, permesso)
    );
  }

  async updatePermessoAsync(permesso: ModuloPermesso) {
    return firstValueFrom(this.http.post<ModuloPermesso>(`${environment.localApiUrl}license/Licenze/UpdatePermesso`, permesso));
  }

  async updateDatePermessoAsync(organizzazioneId: number, activationDate: Date, expiryDate: Date) {
    return firstValueFrom(this.http.post<{result: string}>(`${environment.localApiUrl}license/Licenze/UpdateDatePermessi`, {organizzazioneId, activationDate, expiryDate}));
  }

  async deletePermessoAsync(moduloId: number, orgId: number, roleId: number, operatoreId: number) {
    return firstValueFrom(this.http
      .get<{result: string}>(`${environment.localApiUrl}license/Licenze/DeletePermesso?moduloId=${moduloId}&orgId=${orgId}&roleId=${roleId}&operatoreId=${operatoreId}`));
  }

  async getLicenzaFromIdAsync(orgId: number) {
    return firstValueFrom(this.http.get<Licenza>(`${environment.localApiUrl}license/Licenze/LicenzaFromId?organizzazioneId=${orgId}`));
  }

  async createLicenzaAsync(licenza: Licenza) {
    return firstValueFrom(this.http.post<Licenza>(`${environment.localApiUrl}license/Licenze/CreateLicenza`, licenza));
  }

  async updateLicenzaAsync(licenza: Licenza) {
    return firstValueFrom(this.http.post<Licenza>(`${environment.localApiUrl}license/Licenze/UpdateLicenza`, licenza));
  }

  async deleteLicenzaAsync(orgId: number) {
    return firstValueFrom(this.http
      .get<{result: string}>(`${environment.localApiUrl}license/Licenze/DeleteLicenza?organizzazioneId=${orgId}`));
  }

  /** false se nel frattempo la sessione è stata chiusa: il risultato va scartato, non scritto in cache. */
  private isSameSession(epoch: number): boolean {
    return epoch === this.sessionEpoch;
  }

  /** true se il dato è stato caricato (loadedAt > 0) ed è ancora entro il TTL. */
  private isFresh(loadedAt: number): boolean {
    return loadedAt > 0 && (Date.now() - loadedAt) < LicenzeService.cacheTtlMs;
  }

  /** Carica l'intero stato del menù, riusando opzionalmente i moduli già ottenuti dal login. */
  private loadCompleteMenuStateAsync(orgId: number, useCachedPermessi: boolean): Promise<Modulo[]> {
    const inCorso = this.refreshModuliPromises.get(orgId);
    if (inCorso) {
      return inCorso;
    }

    const epoch = this.sessionEpoch;
    const promise: Promise<Modulo[]> = Promise.all([
      this.loadPermessiAsync(orgId, useCachedPermessi),
      this.bindingsForRefresh(),
      this.refreshMenuConfigAsync(orgId)
    ])
      .then(([moduli]) => {
        if (this.isSameSession(epoch)) {
          this.moduliLoadedForOrgId = orgId;
          this.moduliLoadedAt = Date.now();
        }
        return moduli;
      })
      .finally(() => {
        if (this.refreshModuliPromises.get(orgId) === promise) {
          this.refreshModuliPromises.delete(orgId);
        }
      });

    this.refreshModuliPromises.set(orgId, promise);
    return promise;
  }

  /** Carica e pubblica i moduli, condividendo una richiesta HTTP già in corso per la stessa org. */
  private loadPermessiAsync(orgId: number, useCache: boolean): Promise<Modulo[]> {
    if (useCache && this.permessiLoadedForOrgId === orgId && this.isFresh(this.permessiLoadedAt)) {
      return Promise.resolve(this.currentPermessiSubject.value);
    }

    const inCorso = this.permessiPromises.get(orgId);
    if (useCache && inCorso) {
      return inCorso;
    }

    const epoch = this.sessionEpoch;
    const promise: Promise<Modulo[]> = this.getModuliOfOrgAsync(orgId)
      .then((result) => {
        // Un refresh esplicito può sostituire una richiesta di bootstrap partita prima di una
        // scrittura: in quel caso la risposta più vecchia non deve sovrascrivere quella fresca.
        if (this.isSameSession(epoch) && this.permessiPromises.get(orgId) === promise) {
          this.currentPermessiSubject.next(result);
          this.permessiLoadedForOrgId = orgId;
          this.permessiLoadedAt = Date.now();
        }
        return result;
      })
      .catch((err) => {
        console.error(err);
        throw err;
      })
      .finally(() => {
        if (this.permessiPromises.get(orgId) === promise) {
          this.permessiPromises.delete(orgId);
        }
      });

    this.permessiPromises.set(orgId, promise);
    return promise;
  }

  /** Binding per refreshModuliOfOrgAsync: cache entro il TTL, con fetch condiviso se già in corso. */
  private bindingsForRefresh(): Promise<MenuVoceBinding[]> {
    if (this.isFresh(this.menuBindingsLoadedAt)) {
      return Promise.resolve(this.currentMenuBindingsSubject.value);
    }

    if (!this.menuBindingsPromise) {
      const promise: Promise<MenuVoceBinding[]> = this.refreshMenuBindingsAsync()
        .finally(() => {
          if (this.menuBindingsPromise === promise) {
            this.menuBindingsPromise = null;
          }
        });
      this.menuBindingsPromise = promise;
    }

    return this.menuBindingsPromise;
  }
}
